import type { Root } from "react-dom/client";
import dig from "./dig";
import snakeCaseToCamelCaseObjectKeys from "./snakeCaseToCamelCaseObjectKeys";
import "./request-idle-callback";

import type { ComponentTree, ReactRenderable } from "../global.d";

const REACT_APP_SELECTOR = "[data-react-app]";

let observer: MutationObserver | null;
const roots = new WeakMap<HTMLElement, Root>();

/**
 * Used to mount a component into a HTMLElement based on the data-react-app property set
 * on the element. Uses dot-notation to find the specific component in the provided component
 * tree. For example, if you Specify `data-react-app="One.Two.Three"`, it will attempt to read
 * `components.One.Two.Three` and render the component into the element. If it cannot find the
 * specified component it will issue a warning.
 *
 * @param element An HTMLElement with data-react-app and (optionally) data-react-props set
 * @param components An object containing a tree of components to search
 */
const mountComponent = async function (
  element: HTMLElement,
  components: ComponentTree,
) {
  const appName = element.dataset.reactApp;

  if (!appName) {
    return Promise.reject(new Error(`Missing ${REACT_APP_SELECTOR} attribute`));
  }

  // Coerce the possible-Promise to a non-Promise
  const unwrappedComponents = await components;

  const props = snakeCaseToCamelCaseObjectKeys(
    JSON.parse(element.dataset.reactProps || "{}"),
  );
  const App = dig<ReactRenderable>(appName, unwrappedComponents);

  if (!App) {
    return Promise.reject(
      new Error(`Attempted to load unknown React app - ${appName}`),
    );
  }

  const [React, ReactDOM] = await Promise.all([
    import("react"),
    import("react-dom/client"),
  ]);

  return new Promise((resolve) => {
    const root = ReactDOM.createRoot(element);
    roots.set(element, root);
    root.render(React.createElement(App, props));
    requestIdleCallback(() => resolve(element));
  });
};

/**
 * This will search the given rootElement for elements with `data-react-app`,including
 * the rootElement itself.
 * @param rootElement the element to begin searching from
 */
const findReactAppElements = function (rootElement: HTMLElement | Document) {
  const result = Array.from(
    rootElement.querySelectorAll<HTMLElement>(REACT_APP_SELECTOR),
  );

  // Is the root element a React app?
  if (
    rootElement instanceof HTMLElement &&
    rootElement.matches(REACT_APP_SELECTOR)
  ) {
    result.push(rootElement);
  }

  return result;
};

/**
 * This will search the given rootElement for elements with `data-react-app` and attempt to
 * render them with the components.
 * @param rootElement the element to begin searching from
 * @param components an object containing a tree of React.Component subclasses
 */
const mountComponents = function (
  rootElement: HTMLElement | Document,
  components: ComponentTree,
) {
  return Promise.all(
    findReactAppElements(rootElement).map((element) =>
      mountComponent(element, components),
    ),
  );
};

/**
 * This will search the given rootElement for elements with `data-react-app` and attempt to
 * unmount them.
 * @param rootElement the element to begin searching from
 */
const unmountComponents = async function (rootElement: HTMLElement | Document) {
  const elements = findReactAppElements(rootElement);
  if (elements.length === 0) {
    return;
  }

  elements.forEach((element) => {
    const root = roots.get(element);
    if (root) {
      root.unmount();
      roots.delete(element);
    }
  });
};

/**
 * Creates a MutationObserver to initialise dynamically created React app elements in the
 * document.
 * @param rootElement the element to begin searching from
 * @param components an object containing a tree of React.Component subclasses
 */
const createObserver = function (
  rootElement: HTMLElement | Document,
  components: ComponentTree,
) {
  observer = new MutationObserver((mutationsList, _observer) => {
    mutationsList.forEach((mutation) => {
      Array.from(mutation.addedNodes)
        .filter((addedNode) => addedNode.nodeType === Node.ELEMENT_NODE)
        .forEach((addedNode) =>
          mountComponents(addedNode as HTMLElement, components),
        );
      Array.from(mutation.removedNodes)
        .filter((removedNode) => removedNode.nodeType === Node.ELEMENT_NODE)
        .forEach((removedNode) =>
          unmountComponents(removedNode as HTMLElement),
        );
    });
  });
  observer.observe(rootElement, { childList: true, subtree: true });
};

/**
 * Initalises component mounting on the given element, including monitoring the DOM for
 * new elements with the required data attributes for dynamic mounting.
 * @param rootElement the element to begin searching from
 * @param components an object containing a tree of React.Component subclasses
 */
const initialize = function (
  rootElement: HTMLElement | Document,
  components: ComponentTree,
) {
  createObserver(rootElement, components);
  return mountComponents(rootElement, components);
};

/**
 * Removes the MutationObserver from the document and stops watching for DOM changes.
 */
const uninitialize = function () {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
};

export {
  REACT_APP_SELECTOR,
  initialize,
  uninitialize,
  mountComponent,
  mountComponents,
  unmountComponents,
};
